// File: src/components/MapPage.tsx
import React, { useEffect, useState, useMemo, useRef } from 'react';
import ReactDOMServer from 'react-dom/server';

import { api, apiFetch } from "../utils/api.ts";

import L from 'leaflet';
import { MapContainer, TileLayer, Polyline, CircleMarker, Circle, Marker, Pane, useMapEvents, useMap } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';

import simplify from 'simplify-js';
import haversine from 'haversine-distance';

import Divider from '@mui/material/Divider';
import { IconButton, Box, Slider } from '@mui/material';
import AreaChartIcon from '@mui/icons-material/AreaChart';
import BarChartIcon from '@mui/icons-material/BarChart';
import FilterAltIcon from '@mui/icons-material/FilterAlt';
import EditIcon from '@mui/icons-material/Edit';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import MapIcon from '@mui/icons-material/Map';
import PersonIcon from '@mui/icons-material/Person';
import PlaceIcon from '@mui/icons-material/Place';
import SettingsIcon from '@mui/icons-material/Settings';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { red, pink, purple, deepPurple, indigo, blue, lightBlue, cyan, teal, green, lightGreen, lime, yellow, amber, orange, deepOrange, brown, grey, blueGrey } from '@mui/material/colors';

import CustomDrawer from './CustomDrawer.tsx';
import UserMenu from './UserMenu.tsx';
import PathCharts from './PathCharts.tsx';
import PathStatistics from './PathStatistics.tsx';
import PathFilters from './PathFilters.tsx';
import MapPreferences from './MapPreferences.tsx';
import PositionPopup from './PositionPopup.tsx';
import PoiManager from './PoiManager.tsx';
import ImportDrawer from './ImportDrawer.tsx';
import Icons from './Icons.tsx';
import TagsManager from './TagsManager.tsx';
import MarkerShape, { type MarkerShapeProps, type MarkerShapeType } from './MarkerShape.tsx';
import { TagsProvider, useTagsRegistry } from './TagsProvider.tsx';
import { getTagInCategory, getAncestorAtLevel, resolveEffectiveStyle, dashArrayFor, aggregatedTagIds } from '../utils/tags.ts';

import { ndjsonStream } from '../utils/ndjson.ts';
import StreamingPath, { StreamingPathHandle } from "./StreamingPath.tsx";

import type { Position } from '../types/position.ts';

import { PositionEditingProvider, usePositionEditing } from './PositionEditingProvider.tsx';
import PositionEditing from './PositionEditing.tsx';

type MarkerDrawOptions = L.PathOptions & { shape?: MarkerShapeType | 'none'; radius?: number };

const markerIconCache = new Map<string, L.DivIcon>();

function markerShapeIcon(type: MarkerShapeType, size: number, paint: Omit<MarkerShapeProps, 'type' | 'size'>): L.DivIcon {
  const key = JSON.stringify([type, size, paint]);
  let icon = markerIconCache.get(key);
  if (!icon) {
    icon = L.divIcon({
      html: ReactDOMServer.renderToStaticMarkup(<MarkerShape type={type} size={size} {...paint} />),
      className: 'shape-marker',
      iconSize: [size, size],
      iconAnchor: [size / 2, size / 2],
    });
    markerIconCache.set(key, icon);
  }
  return icon;
}

interface MapPageProps {
  user: User;
  displayedUser: User;
  onLogout: () => void;
  onSwitchUser: (newUser: User | null) => void;
  config: Record<string, any> | null;
  setConfig: (c: any) => void;
}

interface POI {
  id: number;
  user_id: number;
  name: string;
  description: string;
  latitude: number;
  longitude: number;
  radius: number;
  osm_reference?: string;
  icon?: string;
}

type GroupByField<T> = keyof T;

interface GroupingOptions<T> {
  keys: GroupByField<T>[];
  preserveOrder: boolean;
  styleForGroup?: (sample: T) => L.PathOptions;
}

type Grouped<T> = T[][];

function buildKey<T>(item: T, keys: GroupByField<T>[]): string {
  return keys
    .map((k) => {
      const v = (item as any)[k];
      return v === null || v === undefined ? '__NULL__' : String(v);
    })
    .join('::');
}

function groupByAttributes<T>(
  items: T[],
  options: GroupingOptions<T>
): Grouped<T> {
  const { attributes, preserveOrder } = options;

  if (items.length === 0 || Object.keys(attributes).length === 0) return [items.slice()];

  if (preserveOrder) {
    const groups: Grouped<T> = [];
    let currentGroup: T[] = [];
    let currentKey = buildKey(items[0], Object.keys(attributes));

    /* remove the lastPrev part and put it in the other function then centering should be fixed */
    for (const item of items) {
      const key = buildKey(item, Object.keys(attributes));
      if (key !== currentKey && currentGroup.length > 0) {
        groups.push(currentGroup);
        const lastPrev = {
          ...currentGroup[currentGroup.length - 1],
          fromPreviousGroup: true
        };
        currentGroup = [lastPrev, item];
        currentKey = key;
      } else {
        currentGroup.push(item);
      }
    }

    if (currentGroup.length > 0) groups.push(currentGroup);
    return groups;
  } else {
    const map = new Map<string, T[]>();
    for (const item of items) {
      const key = buildKey(item, Object.keys(attributes));
      let arr = map.get(key);
      if (!arr) {
        arr = [];
        map.set(key, arr);
      }
      arr.push(item);
    }
    return Array.from(map.values());
  }
}

const ZoomTracker = ({
  onZoom
}: {
  onZoom: (zoom: number) => void
}) => {
  useMapEvents({
    zoomend(mapEvent: { target: { getZoom: () => any; }; }) {
      const zoom = mapEvent.target.getZoom();
      onZoom(zoom);
    }
  });
  return null;
};

const MoveTracker = ({
  onMove
}: {
  onMove: (bounds: L.LatLngBounds) => void
}) => {
  useMapEvents({
    moveend(mapEvent: { target: { getBounds: () => any; }; }) {
      const bounds = mapEvent.target.getBounds();
      onMove(bounds.pad(0.5));
    }
  });
  return null;
};

const FlyToBoundsHandler: React.FC<{
  positions: Position[];
  flyToBoundsVersion: number;
  setIsFlying: (val: boolean) => void;
  maxZoom?: number;
}> = ({
  positions,
  flyToBoundsVersion,
  setIsFlying,
  maxZoom = 18
}) => {
  const map = useMap();
  const positionsRef = React.useRef<Position[]>(positions);

  useEffect(() => {
    positionsRef.current = positions;
  }, [positions]);

  useEffect(() => {
    const latestPositions = positionsRef.current;
    if (!latestPositions.length) return;

    const bounds = L.latLngBounds(
      latestPositions.map((p: { latitude: number; longitude: number; }) => [p.latitude, p.longitude] as [number, number])
    );

    setIsFlying(true);
    map.flyToBounds(bounds, { padding: [20, 20], maxZoom: maxZoom });

    const onEnd = () => {
      setIsFlying(false);
      map.off("moveend", onEnd);
    };

    map.on("moveend", onEnd);
    return () => {
      map.off("moveend", onEnd);
      setIsFlying(false);
    };
  }, [map, flyToBoundsVersion, setIsFlying, maxZoom]);
  return null;
};

const MapResizer = ({ openDrawers }: { openDrawers: number }) => {
  const map = useMap();
  useEffect(() => {
    map.invalidateSize();
  }, [openDrawers]);
  return null;
};

const MapPathAndMarkerLayer: React.FC<{
  allPositions: Position[];
  visiblePositions: Position[] | Position[][];
  visiblePathSegments: any;
  groupingConfig: GroupingOptions<Position>;
  onMarkerClick: (position: Position) => void;
  showSelection: boolean;
  showPath: boolean;
  showMarker: boolean;
}> = ({ allPositions, visiblePositions, visiblePathSegments, groupingConfig, onMarkerClick, showSelection, showPath, showMarker }) => {
  const editing = usePositionEditing();

  const selectedSegmentPositions = React.useMemo(() => {
    if (!showSelection) return new Set<Position>();
    const startIndex = editing.startIndex;
    const endIndex = editing.endIndex;
    if (startIndex == null || endIndex == null || !allPositions.length) return new Set<Position>();

    const low = Math.max(0, Math.min(startIndex, endIndex));
    const high = Math.min(allPositions.length - 1, Math.max(startIndex, endIndex));
    const selected = new Set<Position>();
    for (let i = low; i <= high; i++) {
      const position = allPositions[i];
      if (position) selected.add(position);
    }
    return selected;
  }, [showSelection, editing.startIndex, editing.endIndex, allPositions]);

  const hasSelection = showSelection && selectedSegmentPositions.size > 0;

  const isSelectedPosition = React.useCallback(
    (position: Position) => selectedSegmentPositions.has(position),
    [selectedSegmentPositions]
  );

  const pathOptionsFor = React.useCallback(
    (representative: Position) => {
      const base = groupingConfig.styleForGroup!(representative)['path'];
      const opacity = hasSelection ? Math.max((base.opacity ?? 1) * 0.4, 0.35) : (base.opacity ?? 1);
      const weight = base.weight ?? 4;
      return {
        ...base,
        opacity,
        weight,
      };
    },
    [groupingConfig, hasSelection]
  );

  const markerOptionsFor = React.useCallback(
    (position: Position, selected: boolean) => {
      const base = groupingConfig.styleForGroup!(position)['marker'] as MarkerDrawOptions;
      return {
        ...base,
        opacity: selected ? 1 : (hasSelection ? Math.max((base.opacity ?? 1) * 0.3, 0.18) : (base.opacity ?? 1)),
        fillOpacity: selected ? (base.fillOpacity ?? 1) : (hasSelection ? Math.max((base.fillOpacity ?? 1) * 0.3, 0.18) : (base.fillOpacity ?? 1)),
      };
    },
    [groupingConfig, hasSelection]
  );

  const renderPositionMarker = (position: Position, selected: boolean, key: string) => {
    const opts = markerOptionsFor(position, selected);
    const type = opts.shape ?? 'circle';
    if (type === 'none') return null;
    const pane = selected ? 'selectedMarkers' : 'markers';
    const eventHandlers = { click: () => onMarkerClick(position) };
    const radius = selected ? 7 : (opts.radius ?? 4);
    const center: [number, number] = [position.latitude, position.longitude];
    if (type === 'circle') {
      return <CircleMarker key={key} center={center} radius={radius} pathOptions={opts} pane={pane} eventHandlers={eventHandlers} />;
    }
    const stroke = opts.color ?? '#3388ff';
    const icon = markerShapeIcon(type, 2 * radius + 2, {
      fill: opts.fillColor ?? stroke,
      fillOpacity: opts.fillOpacity ?? 0.2,
      stroke,
      strokeWidth: opts.weight ?? 3,
      strokeOpacity: opts.opacity ?? 1,
      dashArray: opts.dashArray,
    });
    return <Marker key={key} position={center} icon={icon} pane={pane} eventHandlers={eventHandlers} />;
  };

  const selectedPathSegments = React.useMemo(() => {
    if (!hasSelection) return [] as Array<{ positions: [number, number][]; color: string }>;

    const startIndex = editing.startIndex ?? 0;
    const endIndex = editing.endIndex ?? 0;
    const low = Math.max(0, Math.min(startIndex, endIndex));
    const high = Math.min(allPositions.length - 1, Math.max(startIndex, endIndex));

    const segments: Array<{ positions: [number, number][]; color: string }> = [];
    let currentColor: string | undefined;
    let currentPositions: [number, number][] = [];

    for (let i = low; i <= high; i++) {
      const position = allPositions[i];
      if (!position) continue;
      const style = groupingConfig.styleForGroup!(position)['path'];
      const color = style?.color ?? red[900];

      if (!currentColor || currentColor !== color) {
        if (currentPositions.length > 0) {
          segments.push({ positions: currentPositions, color: currentColor ?? red[900] });
        }
        currentColor = color;
        currentPositions = [];
      }

      currentPositions.push([position.latitude, position.longitude]);
    }

    if (currentPositions.length > 0) {
      segments.push({ positions: currentPositions, color: currentColor ?? red[900] });
    }

    return segments;
  }, [allPositions, editing.startIndex, editing.endIndex, groupingConfig, hasSelection]);

  return (
    <>
      {Array.isArray(visiblePositions[0]) && visiblePositions[0]?.length > 0 ? (
        <>
          {showPath && visiblePathSegments.map((group: { path: any[]; }, groupIdx: number) => {
            return group.path.map((seg: any, segIdx: number) => {
              const representative = visiblePositions[groupIdx]?.[0] as Position;
              return (
                <Polyline
                  key={`segment-${groupIdx}-${segIdx}`}
                  positions={seg}
                  pathOptions={pathOptionsFor(representative)}
                  pane="paths"
                />
              );
            });
          })}

          {showMarker && (visiblePositions as Position[][]).map((group, groupIdx) => {
            return group.map((position, i) => {
              const selected = isSelectedPosition(position);
              return renderPositionMarker(position, selected, `position-marker-${groupIdx}-${i}`);
            });
          })}
        </>
      ) : (
        <>
          {showPath && visiblePathSegments.path.map((seg: any, segIdx: any) => {
            const representative = (visiblePositions as Position[])[0] as Position;
            return (
              <Polyline
                key={`segment-${segIdx}`}
                positions={seg}
                pathOptions={pathOptionsFor(representative)}
                pane="paths"
              />
            );
          })}
          {showMarker && (visiblePositions as Position[]).map((position, i) => {
            const selected = isSelectedPosition(position);
            return renderPositionMarker(position, selected, `position-marker-${i}`);
          })}
        </>
      )}
      {showPath && selectedPathSegments.map((segment, segmentIndex) => (
        segment.positions.length > 1 ? (
          <Polyline
            key={`selected-path-${segmentIndex}`}
            positions={segment.positions}
            pathOptions={{
              color: segment.color,
              weight: 6,
              opacity: 0.95,
              lineCap: 'round',
              lineJoin: 'round',
            }}
            pane="selection"
          />
        ) : null
      ))}
    </>
  );
};

const createMarkerIcon = (icon: string, color: string) => {
  const iconHtml = ReactDOMServer.renderToStaticMarkup(
    <Box style={{
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      color: color,
      width: '24px',
      height: '24px',
    }}>
      {Icons.pois.inline[icon.toLowerCase()]}
    </Box>
  );
  return L.divIcon({
    html: iconHtml,
    className: '',
    iconSize: [24, 24],
    iconAnchor: [12, 12]
  });
};

function buildVisibleSegments(
  positions: Position[] | Position[][],
  bounds: L.LatLngBounds
) {
  if (Array.isArray(positions[0])) {
    return (positions as Position[][]).map(group => buildVisibleSegments(group, bounds)).filter(g => g.length > 0);
  }

  const segments = [];
  let current: Position[] = [];

  for (let i = 0; i < positions.length; i++) {
    const p = positions[i];
    const inB = bounds.contains([p.latitude, p.longitude]);

    if (inB) {
      // start a segment if needed
      if (current.length === 0 && i > 0) current.push(positions[i - 1]); // include previous to avoid a hard cut
      current.push(p);
      // if next goes out, also include it to finish the segment gracefully
      const next = positions[i + 1];
      if (next && !bounds.contains([next.latitude, next.longitude])) {
        current.push(next);
      }
    } else if (current.length > 0) {
      segments.push(current);
      current = [];
    }
  }
  if (current.length > 0) segments.push(current);
  return segments;
}

function exponentialZoomToTolerance(zoomLevel: number, smoothingFactor: number) {
    const a = 2.72281753;
    const b = 0.69350331;
    const bAdjusted = b * (1.6 - smoothingFactor * (1.6 - 0.2)); // smoothingFactor should be settable for user between 0 and 1, but required values for exponential function are between 1.8 and 0.2
    return a * (Math.exp(-bAdjusted * zoomLevel) - Math.exp(-bAdjusted * 18));
}

// function logisticZoomToTolerance(zoomLevel, smoothingFactor) {
//     const L = 106343.549;
//     const k = -0.69504245;
//     const x0 = -15.1994419;
//     const kAdjusted = k * smoothingFactor;
//     return L / (1 + Math.exp(-kAdjusted * (zoomLevel - x0)));
// }

// meters-per-pixel at equator, zoom 0
const metersPerPixelEquator = 156543.03392;

function metersPerPixel(
  metersPerPixelEquator: number,
  latitude: number,
  zoom: number
) {
  return metersPerPixelEquator * Math.cos((latitude * Math.PI) / 180) / Math.pow(2, zoom);
}

// pixelRadiusFromMeters
// Input: meters per pixel at equator, poi radius in meters, poi latitude, zoom
// Output: how many pixels it would appear on screen
// Purpose: "If I stay at my current zoom, how big will this radius look?"
// You use pixelRadiusFromMeters to check at the current zoom whether a POI is “big enough” to render.
function pixelRadiusFromMeters(
  metersPerPixelEquator: number,
  radiusMeters: number,
  latitude: number,
  zoom: number
) {
  return radiusMeters / metersPerPixel(metersPerPixelEquator, latitude, zoom);
}

function meterRadiusFromPixels(
  metersPerPixelEquator: number,
  radiusPixels: number,
  latitude: number,
  zoom: number
) {
  return radiusPixels * metersPerPixel(metersPerPixelEquator, latitude, zoom);
}

// zoomForPixelRadius
// Input: meters per pixel at equator, poi radius in meters, poi latitude, desired pixel size
// Output: zoom level needed to make it look that big
// Purpose: "If I want this radius to be 50px on screen, what zoom do I need?"
// You use zoomForPixelRadius when you want to jump/fit the map to a zoom where the POI appears a desired size.
function zoomForPixelRadius(
  metersPerPixelEquator: number,
  radiusMeters: number,
  latitude: number,
  targetPixel: number
) {
  return Math.log2(metersPerPixelEquator * Math.cos((latitude * Math.PI) / 180) * targetPixel / radiusMeters);
}

// function filterPoisByZoomLevel(pois, zoomLevel) {
//   return pois.filter(poi => {
//     const poiRadiusInPixels = pixelRadiusFromMeters(metersPerPixelEquator, poi.radius, poi.latitude, zoomLevel);
//     return (poiRadiusInPixels > 8); // && poiRadiusInPixels < 128);
//   });
// }

function filterPoisByPositions(pois: any[], positions: { filter: (arg0: (pos: any) => boolean) => { (): any; new(): any; length: number; }; }) {
  return pois.filter((poi: { latitude: any; longitude: any; radius: number; }) => {
    return positions.filter((pos: { latitude: any; longitude: any; }) => {
      const dist = haversine(
        { lat: pos.latitude, lon: pos.longitude },
        { lat: poi.latitude, lon: poi.longitude }
      );
      return (dist <= poi.radius);
    }).length > 0
  });
}

function centerPositionsToGroups(positions: any[], groupingConfig: { attributes: any; }, prevPositions?: string | any[] | undefined) {
  if (Array.isArray(positions[0])) {
    return positions.map((group: any, groupIdx: number) => {
      let prevPositions = groupIdx > 0 ? positions[groupIdx - 1] : undefined;
      return centerPositionsToGroups(group, groupingConfig, prevPositions);
    });
  }
  
  const { attributes } = groupingConfig;

  let prevPositionsCenter = undefined;

  if (Array.isArray(prevPositions)) {
    let prevSumLatitude = 0;
    let prevSumLongitude = 0;
    for (const pos of prevPositions) {
      prevSumLatitude += pos.latitude;
      prevSumLongitude += pos.longitude;
    }
    prevPositionsCenter = {
      latitude:  prevSumLatitude  / prevPositions.length,
      longitude: prevSumLongitude / prevPositions.length
    }
  }

  let sumLatitude = 0;
  let sumLongitude = 0;
  for (const pos of positions) {
    sumLatitude += pos.latitude;
    sumLongitude += pos.longitude;
  }
  const positionsCenter = {
    latitude:  sumLatitude  / positions.length,
    longitude: sumLongitude / positions.length
  }

  const centeredPositions = positions.map((pos: { [x: string]: any; fromPreviousGroup: any; }) => {
    let centerPosition = true;
    for (const attribute of Object.entries(groupingConfig.attributes)) {
      centerPosition = centerPosition && attribute[1].center.includes(pos[attribute[0]]);
    }
    return centerPosition ? (
      !pos.fromPreviousGroup ? 
      {...pos, ...positionsCenter} :
      {...pos, ...prevPositionsCenter}
    ) : pos;
  });
  return centeredPositions;
}

function centerPositionsToPois(positions: any[], pois: any) {
  if (Array.isArray(positions[0])) {
    return positions.map((group: any) => centerPositionsToPois(group, pois));
  }

  return positions.map((pos: { latitude: any; longitude: any; }) => {
    for (const poi of pois) {
      const dist = haversine(
        { lat: pos.latitude, lon: pos.longitude },
        { lat: poi.latitude, lon: poi.longitude }
      );
      if (dist <= poi.displayRadius) {
        return {
          ...pos,
          latitude: poi.latitude,
          longitude: poi.longitude
        };
      }
    }
    return pos;
  });
}

function filterOverlappingPois(pois: POI[]): { keptPois: POI[]; suppressedPois: { poi: POI; suppressedPoisBy: number }[] } {
  const sorted = [...pois].sort((a, b) => b.radius - a.radius || a.id - b.id);

  const keptPois: POI[] = [];
  const suppressedPois: { poi: POI; suppressedPoisBy: number }[] = [];

  for (const poi of sorted) {
    const isCoveredBy = keptPois.find(k =>
      haversine(
        { lat: poi.latitude, lon: poi.longitude },
        { lat: k.latitude, lon: k.longitude }
      ) <= k.displayRadius
    );
    if (isCoveredBy) {
      suppressedPois.push({ poi, suppressedPoisBy: isCoveredBy.id });
    } else {
      keptPois.push(poi);
    }
  }
  return { keptPois, suppressedPois };
}

function computeAdditionalAttributes(positions: Position[]) {
  let lastTime: number | null = null;
  let lastCoord = null;
  let duration: number | null = null;
  let distance: number | null = null;
  let velocity: number | null = null;
  let acceleration: number | null = null;

  const positionsWithCalculatedAttributes = positions.map((p, i) => {
    const coord = { lat: p.latitude, lon: p.longitude };
    const time = new Date(p.time).getTime();

    if (lastCoord) {
      distance = haversine(lastCoord, coord);
    }

    if (lastTime !== null) {
      duration = (time - lastTime) / 1000;
    }

    if (typeof distance === 'number' && typeof duration === 'number' && duration > 0 ) {
      velocity = distance / duration;
      acceleration = velocity / duration;
    }

    lastCoord = coord;
    lastTime = time;

    return {
      ...p,
      distance: distance,
      duration: duration,
      velocity: velocity,
      acceleration: acceleration,
      count: i + 1
    };
  });

  return positionsWithCalculatedAttributes;
}

function computeAggregatesFromPositionsFlat(positions: Position[]) {
  let totalDistance = 0;
  let totalDuration = 0;
  let lastTime: number | null = null;
  let lastCoord: { lat: number; lon: number } | null = null;
  let sumAltitude = 0;
  let countAltitude = 0;
  let maxAltitude = -Infinity;
  let minAltitude = Infinity;
  let sumSpeed = 0;
  let countSpeed = 0;
  let maxSpeed = -Infinity;
  let minSpeed = Infinity;
  let sumVelocity = 0;
  let countVelocity = 0;
  let maxVelocity = -Infinity;
  let minVelocity = Infinity;
  let sumAcceleration = 0;
  let countAcceleration = 0;
  let maxAcceleration = -Infinity;
  let minAcceleration = Infinity;
  let sumAccuracy = 0;
  let countAccuracy = 0;
  let maxAccuracy = -Infinity;
  let minAccuracy = Infinity;
  let sumBearing = 0;
  let countBearing = 0;
  let maxBearing = -Infinity;
  let minBearing = Infinity;

  const perProvider: Record<string, number> = {};
  const perSource: Record<string, number> = {};
  const perTrack: Record<number, number> = {};
  const perTravelMode: Record<string, number> = {};

  const positionsWithAggregates = positions.map((p, i) => {
    const coord = { lat: p.latitude, lon: p.longitude };
    const time = new Date(p.time).getTime();

    if (lastCoord) {
      const segmentDistance = haversine(lastCoord, coord) / 1000;
      totalDistance += segmentDistance;
    }

    if (lastTime !== null) {
      const delta = (time - lastTime) / 3600000;
      totalDuration += delta;
    }

    lastCoord = coord;
    lastTime = time;

    if (typeof p.altitude === 'number') {
      sumAltitude += p.altitude;
      countAltitude++;
      maxAltitude = Math.max(maxAltitude, p.altitude);
      minAltitude = Math.min(minAltitude, p.altitude);
    }

    if (typeof p.speed === 'number') {
      sumSpeed += p.speed;
      countSpeed++;
      maxSpeed = Math.max(maxSpeed, p.speed);
      minSpeed = Math.min(minSpeed, p.speed);
    }

    if (typeof p.velocity === 'number') {
      sumVelocity += p.velocity;
      countVelocity++;
      maxVelocity = Math.max(maxVelocity, p.velocity);
      minVelocity = Math.min(minVelocity, p.velocity);
    }

    if (typeof p.acceleration === 'number') {
      sumAcceleration += p.acceleration;
      countAcceleration++;
      maxAcceleration = Math.max(maxAcceleration, p.acceleration);
      minAcceleration = Math.min(minAcceleration, p.acceleration);
    }

    if (typeof p.accuracy === 'number') {
      sumAccuracy += p.accuracy;
      countAccuracy++;
      maxAccuracy = Math.max(maxAccuracy, p.accuracy);
      minAccuracy = Math.min(minAccuracy, p.accuracy);
    }

    if (typeof p.bearing === 'number') {
      sumBearing += p.bearing;
      countBearing++;
      maxBearing = Math.max(maxBearing, p.bearing);
      minBearing = Math.min(minBearing, p.bearing);
    }

    const provider = p.provider || 'undefined';
    const source = p.source || 'undefined';
    const track = p.track_id;
    const travelMode = p.travel_mode || 'undefined';

    perProvider[provider] = (perProvider[provider] || 0) + 1;
    perSource[source] = (perSource[source] || 0) + 1;
    perTrack[track] = (perTrack[track] || 0) + 1;
    perTravelMode[travelMode] = (perTravelMode[travelMode] || 0) + 1;

    return {
      ...p,
      accumDistance: totalDistance,
      accumDuration: totalDuration,
      count: i + 1,
      perProvider,
      perSource,
      perTrack,
      perTravelMode,
      maxAltitude: isFinite(maxAltitude) ? maxAltitude : null,
      minAltitude: isFinite(minAltitude) ? minAltitude : null,
      avgAltitude: countAltitude ? sumAltitude / countAltitude : null,
      countAltitude,
      maxSpeed: isFinite(maxSpeed) ? maxSpeed : null,
      minSpeed: isFinite(minSpeed) ? minSpeed : null,
      avgSpeed: countSpeed ? sumSpeed / countSpeed : null,
      countSpeed,
      maxVelocity: isFinite(maxVelocity) ? maxVelocity : null,
      minVelocity: isFinite(minVelocity) ? minVelocity : null,
      avgVelocity: countVelocity ? sumVelocity / countVelocity : null,
      countVelocity,
      maxAcceleration: isFinite(maxAcceleration) ? maxAcceleration : null,
      minAcceleration: isFinite(minAcceleration) ? minAcceleration : null,
      avgAcceleration: countAcceleration ? sumAcceleration / countAcceleration : null,
      countAcceleration,
      maxAccuracy: isFinite(maxAccuracy) ? maxAccuracy : null,
      minAccuracy: isFinite(minAccuracy) ? minAccuracy : null,
      avgAccuracy: countAccuracy ? sumAccuracy / countAccuracy : null,
      countAccuracy,
      maxBearing: isFinite(maxBearing) ? maxBearing : null,
      minBearing: isFinite(minBearing) ? minBearing : null,
      avgBearing: countBearing ? sumBearing / countBearing : null,
      countBearing,
    };
  });

  const stats = {
    totalDistance,
    totalDuration,
    count: positions.length,
    perProvider,
    perSource,
    perTrack,
    perTravelMode,
    maxAltitude: isFinite(maxAltitude) ? maxAltitude : null,
    minAltitude: isFinite(minAltitude) ? minAltitude : null,
    avgAltitude: countAltitude ? sumAltitude / countAltitude : null,
    countAltitude,
    maxSpeed: isFinite(maxSpeed) ? maxSpeed : null,
    minSpeed: isFinite(minSpeed) ? minSpeed : null,
    avgSpeed: countSpeed ? sumSpeed / countSpeed : null,
    countSpeed,
    maxVelocity: isFinite(maxVelocity) ? maxVelocity : null,
    minVelocity: isFinite(minVelocity) ? minVelocity : null,
    avgVelocity: countVelocity ? sumVelocity / countVelocity : null,
    countVelocity,
    maxAcceleration: isFinite(maxAcceleration) ? maxAcceleration : null,
    minAcceleration: isFinite(minAcceleration) ? minAcceleration : null,
    avgAcceleration: countAcceleration ? sumAcceleration / countAcceleration : null,
    countAcceleration,
    maxAccuracy: isFinite(maxAccuracy) ? maxAccuracy : null,
    minAccuracy: isFinite(minAccuracy) ? minAccuracy : null,
    avgAccuracy: countAccuracy ? sumAccuracy / countAccuracy : null,
    countAccuracy,
    maxBearing: isFinite(maxBearing) ? maxBearing : null,
    minBearing: isFinite(minBearing) ? minBearing : null,
    avgBearing: countBearing ? sumBearing / countBearing : null,
    countBearing,
  };

  return { positionsWithAggregates, stats };
}

function computeAggregatesFromGroupStats(groupStats: {}[]) {
  let totalDistance = 0;
  let totalDuration = 0;

  for (const stats of groupStats) {
    totalDistance += stats.totalDistance;
    totalDuration += stats.totalDuration;
  }

  return {
    totalDistance,
    totalDuration,
  };
}

function computeAggregates(
  positions: Position[] | Position[][]
): {
  positionsWithAggregates: Position[] | Position[][];
  stats: ReturnType<typeof computeAggregatesFromPositionsFlat>['stats'];
  groupStats?: ReturnType<typeof computeAggregatesFromPositionsFlat>['stats'][];
} {
  // empty
  if (!positions.length) {
    const emptyStats = {
      totalDistance: 0,
      totalDuration: 0,
      count: 0,
      perProvider: {} as Record<string, number>,
      perSource: {} as Record<string, number>,
      perTrack: {} as Record<number, number>,
      maxAltitude: null,
      minAltitude: null,
      avgAltitude: null,
      countAltitude: 0,
      maxSpeed: null,
      minSpeed: null,
      avgSpeed: null,
      countSpeed: 0,
      maxVelocity: null,
      minVelocity: null,
      avgVelocity: null,
      countVelocity: 0,
      maxAcceleration: null,
      minAcceleration: null,
      avgAcceleration: null,
      countAcceleration: 0,
    };
    return {
      positionsWithAggregates: positions,
      stats: emptyStats,
      groupStats: Array.isArray(positions[0]) ? [] : undefined,
    };
  }

  // nested: Position[][]
  if (Array.isArray(positions[0])) {
    // per-group stats and per-group accumulations
    const groupStats: typeof globalStats[] = [];
    const groupedPositionsWithAggregates: Position[][] = positions.map(group => {
      const { positionsWithAggregates, stats } = computeAggregatesFromPositionsFlat(group);
      groupStats.push(stats);
      return positionsWithAggregates;
    });

    // global stats over flattened positions
    const positionsFlat = Array.from(new Map(positions.flat().map(obj => [obj.id, obj])).values());
    const { stats: globalStatsFromPositionsFlat } = computeAggregatesFromPositionsFlat(positionsFlat);
    const globalStatsFromGroupStats = computeAggregatesFromGroupStats(groupStats);
    const globalStats = {
       ...globalStatsFromPositionsFlat,
       ...globalStatsFromGroupStats
    };

    return {
      positionsWithAggregates: groupedPositionsWithAggregates,
      stats: globalStats,
      groupStats,
    };
  }

  // flat: Position[]
  const { positionsWithAggregates, stats } = computeAggregatesFromPositionsFlat(
    positions as Position[]
  );
  return {
    positionsWithAggregates,
    stats,
  };
}

function toISOStringWithTimezone(date: Date) {
  const timezoneOffset = date.getTimezoneOffset();

  const timezoneOffsetSign = timezoneOffset >= 0 ? "-" : "+";
  const timezoneOffsetHours = String(Math.floor(Math.abs(timezoneOffset) / 60)).padStart(2, "0");
  const timezoneOffsetMinutes = String(Math.abs(timezoneOffset) % 60).padStart(2, "0");

  const dateAdjustedToTimezoneOffset = new Date(date.getTime() - timezoneOffset * 60 * 1000);

  const isoStringWithTimezone = dateAdjustedToTimezoneOffset.toISOString().replace("Z", "") + timezoneOffsetSign + timezoneOffsetHours + ":" + timezoneOffsetMinutes;
  return isoStringWithTimezone;
}

function toDateTimeLocalValue(value: string) {
  const parsed = new Date(value);
  if (!Number.isFinite(parsed.getTime())) {
    return null;
  }
  return toISOStringWithTimezone(parsed).slice(0, 16);
}

const MapPageContent: React.FC<MapPageProps> = ({ user, displayedUser, onLogout, onSwitchUser, config, setConfig }) => {
  const tagsCtx = useTagsRegistry();
  const [positions, setPositions] = useState<Position[]>([]);
  const [positionsDatasetVersion, setPositionsDatasetVersion] = useState(0);
  const [pois, setPois] = useState<POI[]>([]);
  const [zoomLevel, setZoomLevel] = useState<number>(13);
  const [mapBounds, setMapBounds] = useState<L.LatLngBounds | null>(null);
  const [mapReady, setMapReady] = useState<boolean>(false);
  const [isFlying, setIsFlying] = useState(false);
  const [isStreaming, setIsStreaming] = useState(true);
  const [statisticsOpen, setStatisticsOpen] = useState(false);
  const [statisticsPinned, setStatisticsPinned] = useState(false);
  const [preferencesOpen, setPreferencesOpen] = useState(false);
  const [preferencesPinned, setPreferencesPinned] = useState(false);
  const [editingOpen, setEditingOpen] = useState(false);
  const [editingPinned, setEditingPinned] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [importPinned, setImportPinned] = useState(false);
  const [importFullscreen, setImportFullscreen] = useState(false);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [filtersPinned, setFiltersPinned] = useState(false);
  const [chartsOpen, setChartsOpen] = useState(false);
  const [chartsPinned, setChartsPinned] = useState(false);
  const [chartsFullscreen, setChartsFullscreen] = useState(false);
  const [userOpen, setUserOpen] = useState(false);
  const [userPinned, setUserPinned] = useState(false);
  const [timelineOpen, setTimelineOpen] = useState(false);
  const [poiOpen, setPoiOpen] = useState(false);
  const [tagsViewOpen, setTagsViewOpen] = useState(false);
  const [selectedPosition, setSelectedPosition] = useState<Position | null>(null);
  const [viewportHeight, setViewportHeight] = useState<number>(window.innerHeight);
  const [viewportWidth, setViewportWidth] = useState<number>(window.innerWidth);
  const prevDrawerOpenRef = useRef(false);
  const ignoreNextPopupAutoPanRef = useRef(false);

  const livePathRef = useRef<StreamingPathHandle | null>(null);

  useEffect(() => {
    if (!displayedUser?.id) return;
  
    const params = new URLSearchParams();
    if (user.admin) params.set("scope_user_id", String(displayedUser.id));
  
    const url = params.toString() ? `/api/config?${params}` : "/api/config";
  
    api
      .get(url)
      .then((res) => setConfig(res.data))
      .catch((err) => {
        console.error("config load failed", err);
        setConfig(null);
      });
  }, [user.admin, displayedUser?.id, setConfig]);

  useEffect(() => {
    const anyDrawerOpen = editingOpen || statisticsOpen || preferencesOpen || filtersOpen || chartsOpen || userOpen || timelineOpen || poiOpen || importOpen || tagsViewOpen;
    if (selectedPosition && anyDrawerOpen && !prevDrawerOpenRef.current) {
      setSelectedPosition(null);
    }
    prevDrawerOpenRef.current = anyDrawerOpen;
  }, [editingOpen, statisticsOpen, preferencesOpen, filtersOpen, chartsOpen, userOpen, timelineOpen, poiOpen, importOpen, tagsViewOpen, selectedPosition]);

  useEffect(() => {
    const updateDimensions = () => {
      setViewportHeight(window.innerHeight);
      setViewportWidth(window.innerWidth);
    };
    window.addEventListener('resize', updateDimensions);
    window.addEventListener('orientationchange', updateDimensions);
    return () => {
      window.removeEventListener('resize', updateDimensions);
      window.removeEventListener('orientationchange', updateDimensions);
    };
  }, []);

  const startOfDay = new Date();
  startOfDay.setHours(0, 0, 0, 0);
  const endOfDay = new Date();
  endOfDay.setHours(23, 59, 59, 999);

  const [startTime, setStartTime] = useState<string>(toISOStringWithTimezone(startOfDay).slice(0, 16));
  const [endTime, setEndTime] = useState<string>(toISOStringWithTimezone(endOfDay).slice(0, 16));
  const [filters, setFilters] = useState<{ [key: string]: any }>({
    provider: [],
    source: [],
    travel_mode: [],
    accuracyMin: null,
    accuracyMax: null,
    accuracyMode: 'all',
    altitudeMin: null,
    altitudeMax: null,
    altitudeMode: 'all',
    speedMin: null,
    speedMax: null,
    speedMode: 'all',
    bearingMin: null,
    bearingMax: null,
    bearingMode: 'all',
    velocityMin: null,
    velocityMax: null,
    velocityMode: 'all',
    accelerationMin: null,
    accelerationMax: null,
    accelerationMode: 'all'
  });
  const [preferences, setPreferences] = useState<{ [key: string]: any }>({
    smoothingFactor: (1.6 - 1)/(1.6 - 0.2),
    pathType: 'solid',
    pathColor: '019bef',
    pathOpacity: 0.9,
    pathWeight: 4,
    pathDash: '5,5',
    pathShadeChildren: true,
    markerType: 'circle',
    markerRadius: 4,
    markerFillType: 'solid',
    markerFillColor: 'd42219',
    markerFillOpacity: 1.0,
    markerBorderType: 'solid',
    markerBorderColor: 'd42219',
    markerBorderOpacity: 1.0,
    markerBorderWeight: 2,
    markerBorderDash: '5,5',
    markerShadeChildren: true,
    filterProvider: [],
    filterSource: [],
    filterTravelMode: [],
    accuracyMode: 'all',
    altitudeMode: 'all',
    speedMode: 'all',
    bearingMode: 'all',
    velocityMode: 'all',
    accelerationMode: 'all',
    stylingSource: 'travel_mode',
    stylingCategoryId: null,
    stylingCategoryLevel: 'leaf',
  });

  // Initialize preferences from config when it loads
  useEffect(() => {
    if (!config) return;
    
    setPreferences(prev => ({
      ...prev,
      smoothingFactor: config['path_options.smoothing_factor'] ?? prev.smoothingFactor,
      pathType: config['path_options.path_type'] ?? prev.pathType,
      pathColor: config['path_options.path_color'] ?? prev.pathColor,
      pathOpacity: config['path_options.path_opacity'] ?? prev.pathOpacity,
      pathWeight: config['path_options.path_weight'] ?? prev.pathWeight,
      pathDash: config['path_options.path_dash'] ?? prev.pathDash,
      pathShadeChildren: config['path_options.path_shade_children'] ?? prev.pathShadeChildren,
      markerType: config['path_options.marker_type'] ?? prev.markerType,
      markerRadius: config['path_options.marker_radius'] ?? prev.markerRadius,
      markerFillType: config['path_options.marker_fill_type'] ?? prev.markerFillType,
      markerFillColor: config['path_options.marker_fill_color'] ?? prev.markerFillColor,
      markerFillOpacity: config['path_options.marker_fill_opacity'] ?? prev.markerFillOpacity,
      markerBorderType: config['path_options.marker_border_type'] ?? prev.markerBorderType,
      markerBorderColor: config['path_options.marker_border_color'] ?? prev.markerBorderColor,
      markerBorderOpacity: config['path_options.marker_border_opacity'] ?? prev.markerBorderOpacity,
      markerBorderWeight: config['path_options.marker_border_weight'] ?? prev.markerBorderWeight,
      markerBorderDash: config['path_options.marker_border_dash'] ?? prev.markerBorderDash,
      markerShadeChildren: config['path_options.marker_shade_children'] ?? prev.markerShadeChildren,
      filterProvider: config['filters.provider'] ? JSON.parse(config['filters.provider']) : prev.filterProvider,
      filterSource: config['filters.source'] ? JSON.parse(config['filters.source']) : prev.filterSource,
      filterTravelMode: config['filters.travel_mode'] ? JSON.parse(config['filters.travel_mode']) : prev.filterTravelMode,
      accuracyMode: config['filters.accuracy_mode'] ?? prev.accuracyMode,
      altitudeMode: config['filters.altitude_mode'] ?? prev.altitudeMode,
      speedMode: config['filters.speed_mode'] ?? prev.speedMode,
      bearingMode: config['filters.bearing_mode'] ?? prev.bearingMode,
      velocityMode: config['filters.velocity_mode'] ?? prev.velocityMode,
      accelerationMode: config['filters.acceleration_mode'] ?? prev.accelerationMode,
      stylingSource: config['styling.source'] ?? prev.stylingSource,
      stylingCategoryId: config['styling.category_id'] != null ? Number(config['styling.category_id']) : prev.stylingCategoryId,
      stylingCategoryLevel: config['styling.category_level'] ?? prev.stylingCategoryLevel,
    }));
  }, [config]);

  useEffect(() => {
    setTimelineOpen(true);
  }, []);

  // useEffect(() => {
  //   if (!displayedUser?.id || !startTime || !endTime) return;
  //   axios.get('/api/positions', {
  //     params: {
  //       userId: displayedUser.id,
  //       start: new Date(startTime).toISOString(),
  //       end: new Date(endTime).toISOString(),
  //     },
  //   })
  //     .then(res => {
  //       Array.isArray(res.data) ? setPositions(res.data) : setPositions([]);
  //     })
  //     .catch(err => {
  //       console.error(err);
  //       setPositions([]);
  //     });
  // }, [displayedUser?.id, startTime, endTime]);

  useEffect(() => {
    if (!displayedUser?.id || !startTime || !endTime || !config) return;

    const ctrl = new AbortController();

    const paramsBounds = new URLSearchParams({
      start: new Date(startTime).toISOString(),
      end: new Date(endTime).toISOString(),
    });
    if (user.admin) paramsBounds.set("userId", String(displayedUser.id));
    
    const paramsStream = new URLSearchParams({
      start: new Date(startTime).toISOString(),
      end: new Date(endTime).toISOString(),
      batchSize: String(config["positions_stream.fetch_batch_size"]),
    });
    if (user.admin) paramsStream.set("userId", String(displayedUser.id));

    setIsStreaming(true);
    setPositions([]);                  // clear final dataset
    livePathRef.current?.reset();      // clear live polyline

    (async () => {
      try {
        setIsStreaming(true);
        setPositions([]);
        livePathRef.current?.reset();

        try {
          const res = await apiFetch(`/api/positions_bounds?${paramsBounds}`, {
            signal: ctrl.signal,
          });
          if (!res.ok) throw new Error(`HTTP ${res.status}`);
          const positions_bounds = await res.json();

          if (positions_bounds?.empty || positions_bounds?.min_latitude == null) return false;

          const south_west_bound: [number, number] = [positions_bounds.min_latitude, positions_bounds.min_longitude];
          const north_east_bound: [number, number] = [positions_bounds.max_latitude, positions_bounds.max_longitude];
          const bounds = L.latLngBounds(south_west_bound, north_east_bound);
          
          await livePathRef.current?.preflyToBounds({
            bounds: bounds,
            maxZoom: 16,
            padding: [20, 20],
            fly: false,
          });
        } catch (e) {
          console.warn("prefly failed:", e);
        }

        const res = await apiFetch(`/api/positions_stream?${paramsStream}`, {
          signal: ctrl.signal,
          headers: { Accept: "application/x-ndjson" },
        });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);

        const all: Position[] = [];
        const batch: Position[] = [];
        let lastAppend = performance.now();

        console.log(config);
        for await (const row of ndjsonStream(res)) {
          all.push(row);
          batch.push(row);

          // Throttle appends ~every 150ms or if the batch is large:
          const now = performance.now();
          if (
            batch.length >= config['positions_stream.flush_after_size'] ||
            now - lastAppend > config['positions_stream.flush_after_ms']
          ) {
            // Cheap decimation during streaming (keeps it fast).
            // Keep every kth point so the live path doesn't explode.
            const decimated = batch.filter((_, i) => i % config['positions_stream.flush_every_nth_position'] === 0)
                                   .map(p => [p.latitude, p.longitude] as [number, number]);

            livePathRef.current?.append(decimated);
            batch.length = 0;
            lastAppend = now;
          }
        }

        // Flush leftover live points:
        if (batch.length) {
          const decimated = batch.filter((_, i) => i % config['positions_stream.flush_every_nth_position'] === 0)
                                 .map(p => [p.latitude, p.longitude] as [number, number]);
          livePathRef.current?.append(decimated);
        }

        // Finalize: remove live overlay, then trigger full render once
        setPositions(all);
        setPositionsDatasetVersion(prev => prev + 1);
      } catch (err: any) {
        if (err?.name !== "AbortError") {
          console.error(err);
          livePathRef.current?.clear();
          setPositions([]);
        }
      } finally {
        setIsStreaming(false);
        livePathRef.current?.clear();
      }
    })();

    return () => {
      ctrl.abort();
      // Ensure cleanup if unmounted mid-stream
      livePathRef.current?.clear();
      setIsStreaming(false);
    };
  }, [displayedUser?.id, startTime, endTime, config]);

  useEffect(() => {
    if (!displayedUser?.id) return;
  
    const params = user.admin ? { user_id: displayedUser.id } : undefined;
  
    api
      .get("/api/pois", { params })
      .then((res) => (Array.isArray(res.data) ? setPois(res.data) : setPois([])))
      .catch((err) => {
        console.error(err);
        setPois([]);
      });
  }, [user.admin, displayedUser?.id]);

  const showMainArea = (area: 'timeline' | 'poi' | 'tags') => {
    setTimelineOpen(area === 'timeline');
    setPoiOpen(area === 'poi');
    setTagsViewOpen(area === 'tags');
  };

  const toggleDrawer = (drawer: 'preferences' | 'editing' | 'filters' | 'statistics' | 'charts' | 'user' | 'import') => {
    if (drawer === 'preferences') {
      setPreferencesOpen((prev: any) => {
        const newVal = !prev;
        if (newVal) {
          if (!editingPinned) setEditingOpen(false);
          if (!filtersPinned) setFiltersOpen(false);
          if (!statisticsPinned) setStatisticsOpen(false);
          if (!chartsPinned) setChartsOpen(false);
          if (!userPinned) setUserOpen(false);
          if (!importPinned) setImportOpen(false);
        }
        return newVal;
      });
    }
    if (drawer === 'editing') {
      setEditingOpen((prev: any) => {
        const newVal = !prev;
        if (newVal) {
          if (!preferencesPinned) setPreferencesOpen(false);
          if (!filtersPinned) setFiltersOpen(false);
          if (!statisticsPinned) setStatisticsOpen(false);
          if (!chartsPinned) setChartsOpen(false);
          if (!userPinned) setUserOpen(false);
          if (!importPinned) setImportOpen(false);
        }
        return newVal;
      });
    }
    if (drawer === 'filters') {
      setFiltersOpen((prev: any) => {
        const newVal = !prev;
        if (newVal) {
          if (!preferencesPinned) setPreferencesOpen(false);
          if (!editingPinned) setEditingOpen(false);
          if (!statisticsPinned) setStatisticsOpen(false);
          if (!chartsPinned) setChartsOpen(false);
          if (!userPinned) setUserOpen(false);
          if (!importPinned) setImportOpen(false);
        }
        return newVal;
      });
    }
    if (drawer === 'statistics') {
      setStatisticsOpen((prev: any) => {
        const newVal = !prev;
        if (newVal) {
          if (!preferencesPinned) setPreferencesOpen(false);
          if (!editingPinned) setEditingOpen(false);
          if (!filtersPinned) setFiltersOpen(false);
          if (!chartsPinned) setChartsOpen(false);
          if (!userPinned) setUserOpen(false);
          if (!importPinned) setImportOpen(false);
        }
        return newVal;
      });
    }
    if (drawer === 'charts') {
      setChartsOpen((prev: any) => {
        const newVal = !prev;
        if (newVal) {
          if (!preferencesPinned) setPreferencesOpen(false);
          if (!editingPinned) setEditingOpen(false);
          if (!filtersPinned) setFiltersOpen(false);
          if (!statisticsPinned) setStatisticsOpen(false);
          if (!userPinned) setUserOpen(false);
          if (!importPinned) setImportOpen(false);
        }
        return newVal;
      });
    }
    if (drawer === 'user') {
      setUserOpen((prev: any) => {
        const newVal = !prev;
        if (newVal) {
          if (!preferencesPinned) setPreferencesOpen(false);
          if (!editingPinned) setEditingOpen(false);
          if (!filtersPinned) setFiltersOpen(false);
          if (!statisticsPinned) setStatisticsOpen(false);
          if (!chartsPinned) setChartsOpen(false);
          if (!importPinned) setImportOpen(false);
        }
        return newVal;
      });
    }
    if (drawer === 'import') {
      setImportOpen((prev: any) => {
        const newVal = !prev;
        if (newVal) {
          if (!preferencesPinned) setPreferencesOpen(false);
          if (!editingPinned) setEditingOpen(false);
          if (!filtersPinned) setFiltersOpen(false);
          if (!statisticsPinned) setStatisticsOpen(false);
          if (!chartsPinned) setChartsOpen(false);
          if (!userPinned) setUserOpen(false);
        }
        return newVal;
      });
    }
  };

  const handleSaveConfig = async (key: string, value: any, valueType: 'int' | 'float' | 'string' | 'bool') => {
    try {
      const params: any = {
        scope: 'user',
        key,
        value: String(value),
        value_type: valueType
      };
      
      if (user.admin && displayedUser?.id && displayedUser.id !== user.id) {
        params.scope_user_id = displayedUser.id;
      }
      
      const res = await api.post('/api/config', params);
      setConfig((prev: any) => ({
        ...prev,
        [key]: value
      }));
      return res.data;
    } catch (error) {
      console.error('Failed to save config:', error);
      throw error;
    }
  };

  const relevantPois = useMemo(() => {
    return filterPoisByPositions(pois, positions);
  }, [pois, positions]);

  const enhancedPois = useMemo(() => {
    return relevantPois.map((poi: { latitude: number; }) => {
      return {
        ...poi,
        displayRadius: meterRadiusFromPixels(metersPerPixelEquator, 24, poi.latitude, zoomLevel)
      };
    })
  }, [relevantPois, zoomLevel]);

  const filteredPois = useMemo(() => {
    const { keptPois, suppressedPois } = filterOverlappingPois(enhancedPois);
    return keptPois.filter(poi => poi.radius < poi.displayRadius);
  }, [enhancedPois]);

  const positionsWithCalculatedAttributes = useMemo(() => {
    return computeAdditionalAttributes(positions);
  }, [positions]);

  // Attach a synthetic _stylingTagId (the tag id at the chosen ancestor level
  // in the styling category) so the grouping pipeline can key on it without
  // extra changes.
  const positionsWithStyling = useMemo(() => {
    if (preferences.stylingSource !== 'tag_category' || preferences.stylingCategoryId == null) {
      return positionsWithCalculatedAttributes;
    }
    const catId = preferences.stylingCategoryId as number;
    const level = preferences.stylingCategoryLevel ?? 'leaf';
    return positionsWithCalculatedAttributes.map((p: any) => {
      const assigned = getTagInCategory(p, catId, tagsCtx.registry);
      const styling = getAncestorAtLevel(assigned, level as any, tagsCtx.registry);
      return { ...p, _stylingTagId: styling?.id ?? null };
    });
  }, [positionsWithCalculatedAttributes, preferences.stylingSource, preferences.stylingCategoryId,
      preferences.stylingCategoryLevel, tagsCtx.registry]);

  const categoricalUniqueValues = useMemo(() => {
    const attrs = ['provider', 'source', 'travel_mode'] as const;
    return attrs.reduce((acc, attr) => {
      const values = Array.from(new Set(
        positionsWithCalculatedAttributes.map((p: any) => {
          const val = p[attr];
          return val === null || val === undefined ? 'undefined' : String(val);
        })
      ));
      acc[attr] = values;
      return acc;
    }, {} as Record<string, string[]>);
  }, [positionsWithCalculatedAttributes]);

  const aggregation = useMemo<{ attribute: string; values: Array<string | number> }>(() => {
    if (preferences.stylingSource === 'tag_category' && preferences.stylingCategoryId != null) {
      return { attribute: '_stylingTagId', values: aggregatedTagIds(tagsCtx.registry) };
    }
    return { attribute: 'travel_mode', values: ['stationary'] };
  }, [preferences.stylingSource, preferences.stylingCategoryId, tagsCtx.registry]);

  const groupingConfig = useMemo<GroupingOptions<Position>>(() => {
    const defaults = {
      path: {
        type: preferences.pathType,
        color: `#${preferences.pathColor}`,
        opacity: preferences.pathOpacity,
        weight: preferences.pathWeight,
        dash: preferences.pathDash,
        shadeChildren: preferences.pathShadeChildren,
      },
      marker: {
        type: preferences.markerType,
        radius: preferences.markerRadius,
        fillType: preferences.markerFillType,
        fillColor: `#${preferences.markerFillColor}`,
        fillOpacity: preferences.markerFillOpacity,
        borderType: preferences.markerBorderType,
        borderColor: `#${preferences.markerBorderColor}`,
        borderOpacity: preferences.markerBorderOpacity,
        borderWeight: preferences.markerBorderWeight,
        borderDash: preferences.markerBorderDash,
        shadeChildren: preferences.markerShadeChildren,
      },
    };

    if (preferences.stylingSource === 'tag_category' && preferences.stylingCategoryId != null) {
      return {
        attributes: { _stylingTagId: { center: aggregation.values } },
        preserveOrder: true,
        styleForGroup: (p: any) => {
          const stylingTag = p?._stylingTagId != null ? tagsCtx.registry.tags.get(p._stylingTagId) ?? null : null;
          const style = resolveEffectiveStyle(stylingTag, tagsCtx.registry, defaults);
          const pathOpts: any = {};
          if (style.path.type !== 'none' && style.path.color) {
            pathOpts.color = style.path.color;
            pathOpts.weight = style.path.weight;
            pathOpts.opacity = style.path.opacity;
            const dash = dashArrayFor(style.path.type, style.path.dash);
            if (dash) pathOpts.dashArray = dash;
          }
          const markerOpts: any = { shape: style.marker.type };
          if (style.marker.type !== 'none') {
            markerOpts.radius = style.marker.radius;
            if (style.marker.fillType !== 'none' && style.marker.fillColor) {
              markerOpts.fillColor = style.marker.fillColor;
              markerOpts.fillOpacity = style.marker.fillOpacity;
            } else {
              markerOpts.fillOpacity = 0;
            }
            if (style.marker.borderType !== 'none' && style.marker.borderColor) {
              markerOpts.color = style.marker.borderColor;
              markerOpts.weight = style.marker.borderWeight;
              markerOpts.opacity = style.marker.borderOpacity;
              if (style.marker.borderType === 'dashed' && style.marker.borderDash) markerOpts.dashArray = style.marker.borderDash;
            } else {
              markerOpts.weight = 0;
            }
          }
          return { path: pathOpts, marker: markerOpts };
        },
      };
    }

    return {
      attributes: {
        travel_mode: {
          center: ['stationary']
        }
      },
      preserveOrder: true,
      styleForGroup: (p: { travel_mode: any; }) => {
        if (!p || !p.travel_mode) {
          // Use preferences for undefined travel mode
          const pathOptions: any = {};
          if (preferences.pathType !== 'none') {
            pathOptions.color = `#${preferences.pathColor}`;
            pathOptions.weight = preferences.pathWeight;
            pathOptions.opacity = preferences.pathOpacity;
            const dash = dashArrayFor(preferences.pathType, preferences.pathDash);
            if (dash) pathOptions.dashArray = dash;
          }

          const markerOptions: any = { shape: preferences.markerType };
          if (preferences.markerType !== 'none') {
            markerOptions.radius = preferences.markerRadius;

            // Fill settings
            if (preferences.markerFillType !== 'none') {
              markerOptions.fillColor = `#${preferences.markerFillColor}`;
              markerOptions.fillOpacity = preferences.markerFillOpacity;
            } else {
              markerOptions.fillOpacity = 0;
            }

            // Border settings
            if (preferences.markerBorderType !== 'none') {
              markerOptions.color = `#${preferences.markerBorderColor}`;
              markerOptions.weight = preferences.markerBorderWeight;
              markerOptions.opacity = preferences.markerBorderOpacity;
              if (preferences.markerBorderType === 'dashed') {
                markerOptions.dashArray = preferences.markerBorderDash;
              }
            } else {
              markerOptions.weight = 0;
            }
          }

          return {
            path: pathOptions,
            marker: markerOptions
          };
        }
 
        switch (p.travel_mode) {
          case 'stationary':
            return {
              path: { color: grey[200], weight: 4, opacity: 1 },
              marker: { color: grey[200], fillColor: grey[200], opacity: 1, fillOpacity: 1 }
            };
          case 'foot':
            return {
              path: { color: amber[500], weight: 4, opacity: 1 },
              marker: { color: amber[500], fillColor: amber[500], opacity: 1, fillOpacity: 1 }
            };
          case 'bike':
            return {
              path: { color: green[700], weight: 4, opacity: 1 },
              marker: { color: green[700], fillColor: green[700], opacity: 1, fillOpacity: 1 }
            };
          case 'car':
            return {
              path: { color: pink[600], weight: 4, opacity: 1 },
              marker: { color: pink[600], fillColor: pink[600], opacity: 1, fillOpacity: 1 }
            };
          case 'bus':
            return {
              path: { color: lightBlue[400], weight: 4, opacity: 1 },
              marker: { color: lightBlue[400], fillColor: lightBlue[400], opacity: 1, fillOpacity: 1 }
            };
          case 'tram':
            return {
              path: { color: blue[500], weight: 4, opacity: 1 },
              marker: { color: blue[500], fillColor: blue[500], opacity: 1, fillOpacity: 1 }
            };
          case 'metro':
            return {
              path: { color: blue[800], weight: 4, opacity: 1 },
              marker: { color: blue[800], fillColor: blue[800], opacity: 1, fillOpacity: 1 }
            };
          case 'train':
            return {
              path: { color: indigo[900], weight: 4, opacity: 1 },
              marker: { color: indigo[900], fillColor: indigo[900], opacity: 1, fillOpacity: 1 }
            };
          case 'boat':
            return {
              path: { color: cyan[400], weight: 4, opacity: 1 },
              marker: { color: cyan[400], fillColor: cyan[400], opacity: 1, fillOpacity: 1 }
            };
          case 'plane':
            return {
              path: { color: purple[500], weight: 4, opacity: 1, dashArray: '1 6' },
              marker: { color: purple[500], fillColor: purple[500], opacity: 1, fillOpacity: 1 }
            };
          default:
            return {
              path: { color: red[500], weight: 4, opacity: 1 },
              marker: { color: red[500], fillColor: red[500], opacity: 1, fillOpacity: 1 }
            };
        }
      },
    };
  }, [preferences, tagsCtx.registry, aggregation]);

  const groupedPositionsWithCalculatedAttributes = useMemo(() => {
    return groupByAttributes(positionsWithStyling, groupingConfig);
  }, [positionsWithStyling, groupingConfig]);

  const groupedAndFilteredPositionsWithCalculatedAttributes = useMemo(() => {
    const filterPositions = (p: { provider: string | null; source: string | null; travel_mode: string | null; accuracy: number | null; altitude: number | null; speed: number | null; bearing: number | null; velocity: number | null; acceleration: number | null; }) => {
      const inMultiSelect = (key: string, val: string | null) => {
        const displayVal = val === null ? 'undefined' : val;
        return filters[key].length === 0 || filters[key].includes(displayVal);
      };

      const inRangeWithNullMode = (key: string, val: number | null) => {
        const mode = filters[`${key}Mode`] || 'all';
        const min = filters[`${key}Min`];
        const max = filters[`${key}Max`];

        if (val === null) return mode === 'onlyNull' || mode === 'all';
        if (mode === 'onlyNull') return false;

        return (
          (min === null || val >= min) &&
          (max === null || val <= max)
        );
      };

      const inTagCategoryFilters = () => {
        for (const cat of tagsCtx.registry.effectiveCategories) {
          const key = `tag_category_${cat.id}`;
          const state = filters[key];
          if (!state) continue;
          const tagIds: number[] = state.tagIds ?? [];
          const includeUntagged: boolean = state.includeUntagged !== false;
          if (tagIds.length === 0 && includeUntagged) continue;
          const assignedIds: number[] = ((p as any).tag_ids ?? []).filter((id: number) => {
            const t = tagsCtx.registry.tags.get(id);
            return t?.category_id === cat.id;
          });
          const isUntagged = assignedIds.length === 0;
          if (isUntagged) {
            if (!includeUntagged) return false;
          } else if (!assignedIds.some((id: number) => tagIds.includes(id))) {
            return false;
          }
        }
        return true;
      };

      return (
        inMultiSelect('provider', p.provider) &&
        inMultiSelect('source', p.source) &&
        inMultiSelect('travel_mode', p.travel_mode) &&
        inRangeWithNullMode('accuracy', p.accuracy) &&
        inRangeWithNullMode('altitude', p.altitude) &&
        inRangeWithNullMode('speed', p.speed) &&
        inRangeWithNullMode('bearing', p.bearing) &&
        inRangeWithNullMode('velocity', p.velocity) &&
        inRangeWithNullMode('acceleration', p.acceleration) &&
        inTagCategoryFilters()
      );
    };

    if (Array.isArray(groupedPositionsWithCalculatedAttributes[0])) {
      return groupedPositionsWithCalculatedAttributes
        .map((group: any[]) => group.filter(filterPositions))
        .filter((group: string | any[]) => group.length > 0);
    } else {
      return groupedPositionsWithCalculatedAttributes.filter(filterPositions);
    }
  }, [groupedPositionsWithCalculatedAttributes, filters, tagsCtx.registry]);

  const filteredPositionsWithCalculatedAttributes = useMemo(
    () => groupedAndFilteredPositionsWithCalculatedAttributes.flat(),
    [groupedAndFilteredPositionsWithCalculatedAttributes]
  );

  const groupedAndFilteredPositionsWithCalculatedAttributesCenteredOnGroups = useMemo(() => {
    return centerPositionsToGroups(groupedAndFilteredPositionsWithCalculatedAttributes, groupingConfig);
  }, [groupedAndFilteredPositionsWithCalculatedAttributes, filteredPois, zoomLevel]);

  const groupedAndFilteredPositionsWithCalculatedAttributesCenteredOnGroupsAndPois = useMemo(() => {
    return centerPositionsToPois(groupedAndFilteredPositionsWithCalculatedAttributesCenteredOnGroups, filteredPois);
  }, [groupedAndFilteredPositionsWithCalculatedAttributesCenteredOnGroups, filteredPois, zoomLevel]);

  const {
    groupedAndFilteredPositionsWithCalculatedAttributesAndAggregatesCenteredOnGroupsAndPois,
    pathStatistics,
    pathGroupStatistics
  } = useMemo(() => {
    let groupedAndFilteredPositionsWithCalculatedAttributesAndAggregatesCenteredOnGroupsAndPois;
    let pathStatistics;
    let pathGroupStatistics;

    const res = computeAggregates(groupedAndFilteredPositionsWithCalculatedAttributesCenteredOnGroupsAndPois);
    groupedAndFilteredPositionsWithCalculatedAttributesAndAggregatesCenteredOnGroupsAndPois = res.positionsWithAggregates;
    pathStatistics = res.stats;

    if (Array.isArray(groupedAndFilteredPositionsWithCalculatedAttributesCenteredOnGroupsAndPois[0])) {
      pathGroupStatistics = res.groupStats;
    } else {
      pathGroupStatistics = undefined;
    }

    return {
      groupedAndFilteredPositionsWithCalculatedAttributesAndAggregatesCenteredOnGroupsAndPois,
      pathStatistics,
      pathGroupStatistics
    };
  }, [groupedAndFilteredPositionsWithCalculatedAttributesCenteredOnGroupsAndPois, filters]);

  const groupedAndFilteredPositionsWithCalculatedAttributesAndAggregatesCenteredOnGroupsAndPoisPreparedForPaths = useMemo(() => {
    return groupedAndFilteredPositionsWithCalculatedAttributesAndAggregatesCenteredOnGroupsAndPois.map((group: any, groupIdx: number) => {
      let prevGroup = groupIdx > 0 ? groupedAndFilteredPositionsWithCalculatedAttributesAndAggregatesCenteredOnGroupsAndPois[groupIdx - 1] : undefined;
      let prevGroupLastPosition = groupIdx > 0 ? {
        ...prevGroup[prevGroup.length - 1],
        fromPreviousGroup: true
      } : undefined;
      return group;
      // return groupIdx > 0 ? [prevGroupLastPosition].concat(group) : group;
    });
  }, [groupedAndFilteredPositionsWithCalculatedAttributesAndAggregatesCenteredOnGroupsAndPois]);

  const simplifiedPositions = useMemo(() => {
    const process = (arr: any[]) => {
      const raw = arr.map((p: { longitude: any; latitude: any; }) => ({
        x: p.longitude,
        y: p.latitude,
        data: p
      }));
      if (raw.length === 0) return [];
      const tolerance = exponentialZoomToTolerance(zoomLevel, preferences.smoothingFactor);
      const simplified = simplify(raw, tolerance);
      return simplified.map(p => p.data);
    };
  
    if (Array.isArray(groupedAndFilteredPositionsWithCalculatedAttributesAndAggregatesCenteredOnGroupsAndPoisPreparedForPaths[0])) {
      return groupedAndFilteredPositionsWithCalculatedAttributesAndAggregatesCenteredOnGroupsAndPoisPreparedForPaths.map((group: any) => process(group));
    }
  
    return process(groupedAndFilteredPositionsWithCalculatedAttributesAndAggregatesCenteredOnGroupsAndPoisPreparedForPaths);
  }, [groupedAndFilteredPositionsWithCalculatedAttributesAndAggregatesCenteredOnGroupsAndPoisPreparedForPaths, zoomLevel, preferences]);

  // const visiblePositions = useMemo(() => {
  //   if (mapBounds === null) return simplifiedPositions;

  //   const filterToVisiblePositions = (arr: Position[]) =>
  //     arr.filter(p => mapBounds.contains([p.latitude, p.longitude]));
  // 
  //   if (Array.isArray(simplifiedPositions[0])) {
  //     return (simplifiedPositions as Position[][]).map(group => filterToVisiblePositions(group)).filter(g => g.length > 0);
  //   }
  //   return filterToVisiblePositions(simplifiedPositions as Position[]);
  // }, [simplifiedPositions, mapBounds]);
  
  const visiblePositionSegments = useMemo(() => {
    if (mapBounds === null) return simplifiedPositions;
    return buildVisibleSegments(simplifiedPositions, mapBounds);
  }, [simplifiedPositions, mapBounds]);

  const visiblePositions = useMemo(() => {
    let visiblePositions = [];
    if (Array.isArray(visiblePositionSegments[0]) && Array.isArray(visiblePositionSegments[0][0])) {
       visiblePositions = visiblePositionSegments.map(
         (        group: any[]) => group.map(
          (          seg: any[]) => seg.filter(
            (            pos: { fromPreviousGroup: any; }) => !pos.fromPreviousGroup
          )
        ).filter(
          (          seg: string | any[]) => seg.length > 0
        ).flat()
      ).filter(
        (        group: string | any[]) => group.length > 0
      );
    } else {
      visiblePositions = visiblePositionSegments.filter((seg: string | any[]) => seg.length > 0).flat();
    }
    return visiblePositions;
  }, [visiblePositionSegments]);

  const visiblePathSegments = useMemo(() => {
    let visiblePathSegments = [];
    const toLatLngs = (arr: Position[]) => arr.map(p => [p.latitude, p.longitude] as [number, number]);
    if (Array.isArray(visiblePositionSegments[0]) && Array.isArray(visiblePositionSegments[0][0])) {
      visiblePathSegments = visiblePositionSegments.map(
        (        group: any[]) => ({
          path: group.filter(
            (            seg: { filter: (arg0: (pos: any) => boolean) => { (): any; new(): any; length: number; }; }) => seg.filter(
              (              pos: { fromPreviousGroup: any; }) => !pos.fromPreviousGroup
            ).length > 0
          ).map(
            (            seg: Position[]) => toLatLngs(seg)
          ),
          positionStart: group.flat().filter(
            (            pos: { fromPreviousGroup: any; }) => !pos.fromPreviousGroup
          )[0],
          positionEnd: group.flat().filter(
            (            pos: { fromPreviousGroup: any; }) => !pos.fromPreviousGroup
          )[group.flat().filter(
            (            pos: { fromPreviousGroup: any; }) => !pos.fromPreviousGroup
          ).length - 1],
        })
      ).filter(
        (        group: { path: string | any[]; }) => group.path.length > 0
      );
    } else {
      visiblePathSegments = {
        path: visiblePositionSegments.filter((seg: string | any[]) => seg.length > 0).map((seg: Position[]) => toLatLngs(seg)),
        positionStart: visiblePositionSegments.filter((seg: string | any[]) => seg.length > 0).flat()[0],
        positionEnd: visiblePositionSegments.filter((seg: string | any[]) => seg.length > 0).flat()[
          visiblePositionSegments.filter((seg: string | any[]) => seg.length > 0).flat().length - 1
        ]
      };
    }
    return visiblePathSegments;
  }, [visiblePositionSegments]);

  useEffect(() => {
    if (groupedAndFilteredPositionsWithCalculatedAttributesAndAggregatesCenteredOnGroupsAndPoisPreparedForPaths.length === 0 && groupedAndFilteredPositionsWithCalculatedAttributesAndAggregatesCenteredOnGroupsAndPoisPreparedForPaths[0]?.length === 0) {
      setFiltersOpen(true);
    }
  }, [groupedAndFilteredPositionsWithCalculatedAttributesAndAggregatesCenteredOnGroupsAndPoisPreparedForPaths]);

  return (
    <Box className="relative w-screen overflow-hidden" sx={{ height: viewportHeight }}>
      <Box
        sx={{
          position: 'fixed',
          top: 0,
          right: 0,
          height: viewportHeight,
          width: 56,
          bgcolor: 'white',
          borderLeft: '1px solid #ccc',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'space-between',
          zIndex: (theme) => theme.zIndex.drawer
        }}
      >
        {/* Top Buttons */}
        <Box>
          <IconButton
            onClick={() => toggleDrawer('user')}
            sx={{
              width: 40,
              height: 40,
              p: 0,
              m: 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              mx: 'auto',
              borderRadius: '50%',
              bgcolor: userOpen ? 'primary.dark' : 'primary.main',
              color: userOpen ? 'white' : 'white',
              '&:hover': {
                bgcolor: userOpen ? 'primary.main' : 'primary.light',
                color: userOpen ? 'white' : 'white'
              }
            }}
          >
            <PersonIcon />
          </IconButton>
          <IconButton
            onClick={() => showMainArea('timeline')}
            sx={{
              width: 40,
              height: 40,
              p: 0,
              m: 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              mx: 'auto',
              borderRadius: '50%',
              bgcolor: timelineOpen ? 'error.main' : null,
              color: timelineOpen ? 'white' : null,
              '&:hover': {
                bgcolor: timelineOpen ? 'error.light' : 'error.light',
                color: timelineOpen ? 'white' : 'white'
              }
            }}
          >
            <MapIcon />
          </IconButton>
          <IconButton
            onClick={() => showMainArea('poi')}
            sx={{
              width: 40,
              height: 40,
              p: 0,
              m: 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              mx: 'auto',
              borderRadius: '50%',
              bgcolor: poiOpen ? 'error.main' : null,
              color: poiOpen ? 'white' : null,
              '&:hover': {
                bgcolor: poiOpen ? 'error.light' : 'error.light',
                color: poiOpen ? 'white' : 'white'
              }
            }}
          >
            <PlaceIcon />
          </IconButton>
          <IconButton
            onClick={() => showMainArea('tags')}
            sx={{
              width: 40,
              height: 40,
              p: 0,
              m: 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              mx: 'auto',
              borderRadius: '50%',
              bgcolor: tagsViewOpen ? 'error.main' : null,
              color: tagsViewOpen ? 'white' : null,
              '&:hover': {
                bgcolor: tagsViewOpen ? 'error.light' : 'error.light',
                color: tagsViewOpen ? 'white' : 'white'
              }
            }}
            aria-label="Open Tags"
          >
            <LocalOfferIcon />
          </IconButton>
        </Box>

        {timelineOpen && (
          <Box
            sx={{
              zIndex: 10000,
            }}
          >
            {groupedAndFilteredPositionsWithCalculatedAttributesAndAggregatesCenteredOnGroupsAndPoisPreparedForPaths.length > 0 && groupedAndFilteredPositionsWithCalculatedAttributesAndAggregatesCenteredOnGroupsAndPoisPreparedForPaths[0]?.length > 0 && (
              <>
                <IconButton
                  onClick={() => toggleDrawer('charts')}
                  sx={{
                    width: 40,
                    height: 40,
                    p: 0,
                    m: 1,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mx: 'auto',
                    borderRadius: '50%',
                    bgcolor: chartsOpen ? 'secondary.main' : null,
                    color: chartsOpen ? 'white' : null,
                    '&:hover': {
                      bgcolor: chartsOpen ? 'secondary.light' : 'secondary.light',
                      color: chartsOpen ? 'white' : 'white'
                    }
                  }}
                  aria-label="Open Charts"
                >
                  <AreaChartIcon />
                </IconButton>
                <IconButton
                  onClick={() => toggleDrawer('statistics')}
                  sx={{
                    width: 40,
                    height: 40,
                    p: 0,
                    m: 1,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mx: 'auto',
                    borderRadius: '50%',
                    bgcolor: statisticsOpen ? 'secondary.main' : null,
                    color: statisticsOpen ? 'white' : null,
                    '&:hover': {
                      bgcolor: statisticsOpen ? 'secondary.light' : 'secondary.light',
                      color: statisticsOpen ? 'white' : 'white'
                    }
                  }}
                  aria-label="Open Statistics"
                >
                  <BarChartIcon />
                </IconButton>
              </>
            )}
            <IconButton
              onClick={() => toggleDrawer('editing')}
              sx={{
                width: 40,
                height: 40,
                p: 0,
                m: 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mx: 'auto',
                borderRadius: '50%',
                bgcolor: editingOpen ? 'secondary.main' : null,
                color: editingOpen ? 'white' : null,
                '&:hover': {
                  bgcolor: editingOpen ? 'secondary.light' : 'secondary.light',
                  color: editingOpen ? 'white' : 'white'
                }
              }}
              aria-label="Open Editing"
            >
              <EditIcon />
            </IconButton>
            <IconButton
              onClick={() => toggleDrawer('import')}
              sx={{
                width: 40,
                height: 40,
                p: 0,
                m: 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mx: 'auto',
                borderRadius: '50%',
                bgcolor: importOpen ? 'secondary.main' : null,
                color: importOpen ? 'white' : null,
                '&:hover': {
                  bgcolor: importOpen ? 'secondary.light' : 'secondary.light',
                  color: importOpen ? 'white' : 'white'
                }
              }}
              aria-label="Import Track"
            >
              <CloudUploadIcon />
            </IconButton>
            <IconButton
              onClick={() => toggleDrawer('filters')}
              sx={{
                width: 40,
                height: 40,
                p: 0,
                m: 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mx: 'auto',
                borderRadius: '50%',
                bgcolor: filtersOpen ? 'secondary.main' : null,
                color: filtersOpen ? 'white' : null,
                '&:hover': {
                  bgcolor: filtersOpen ? 'secondary.light' : 'secondary.light',
                  color: filtersOpen ? 'white' : 'white'
                }
              }}
              aria-label="Open Filters"
            >
              <FilterAltIcon />
            </IconButton>
            <IconButton
              onClick={() => toggleDrawer('preferences')}
              sx={{
                width: 40,
                height: 40,
                p: 0,
                m: 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mx: 'auto',
                borderRadius: '50%',
                bgcolor: preferencesOpen ? 'secondary.main' : null,
                color: preferencesOpen ? 'white' : null,
                '&:hover': {
                  bgcolor: preferencesOpen ? 'secondary.light' : 'secondary.light',
                  color: preferencesOpen ? 'white' : 'white'
                }
              }}
              aria-label="Open Preferences"
            >
              <SettingsIcon />
            </IconButton>
          </Box>
        )}
      </Box>

      {/* Sidebar Drawer with UserMenu */}
      <CustomDrawer
        title={user.login}
        icon={Icons.drawer.heading.user}
        anchor="right"
        open={userOpen}
        onClose={() => setUserOpen(false)}
        width="20vw"
        minWidth="320px"
        height={`${viewportHeight}px`}
        minHeight={`${viewportHeight}px`}
        top="0px"
        bottom="0px"
        right="56px"
      >
        <UserMenu
          user={user}
          displayedUser={displayedUser}
          onLogout={onLogout}
          onSwitchUser={onSwitchUser}
        />
      </CustomDrawer>

      {timelineOpen && (
        <PositionEditingProvider positions={filteredPositionsWithCalculatedAttributes} resetSelectionKey={positionsDatasetVersion}>
          <Box sx={{
            height: (chartsPinned && chartsOpen) ? `calc(${viewportHeight}px - max(20vh, 350px))` : `${viewportHeight}px`,
            width:  `calc(100vw - ${(preferencesPinned && preferencesOpen) + (filtersPinned && filtersOpen) + (editingPinned && editingOpen) + (statisticsPinned && statisticsOpen) + (importPinned && importOpen)} * max(20vw, 320px) - 56px)`,
            position: 'absolute',
            zIndex: 0
          }}>
            {/* groupedAndFilteredPositionsWithCalculatedAttributesAndAggregatesCenteredOnGroupsAndPoisPreparedForPaths.length === 0 && livePathRef.length === 0 ? (
              <Box
                sx={{
                  height: '100%',
                  width: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: 'error.main',
                  fontWeight: 500,
                  fontSize: 18,
                  textAlign: 'center',
                  px: 2
                }}
              >
                No position data found for the selected time range or filters.
                <br />
                Please adjust your filters.
              </Box>
            ) : (
            */}
            {
              <MapContainer
                center={[0, 0]}
                zoom={1}
                style={{
                  height: '100%',
                  width: '100%',
                }}
                maxZoom={25}
              >
                <FlyToBoundsHandler
                  positions={positions}
                  displayedUserId={displayedUser?.id ?? ''}
                  startTime={startTime}
                  endTime={endTime}
                  flyToBoundsVersion={positionsDatasetVersion}
                  setIsFlying={setIsFlying}
                />
                <ZoomTracker onZoom={(zoom) => {
                  setZoomLevel(zoom);
                  if (ignoreNextPopupAutoPanRef.current) {
                    ignoreNextPopupAutoPanRef.current = false;
                    return;
                  }
                  setSelectedPosition(null);
                }} />
                <MoveTracker onMove={(bounds) => {
                  setMapBounds(bounds);
                  if (ignoreNextPopupAutoPanRef.current) {
                    ignoreNextPopupAutoPanRef.current = false;
                    return;
                  }
                  setSelectedPosition(null);
                }} />
                <MapResizer openDrawers={
                  ((userOpen && userPinned) ? 1 : 0) +
                  ((chartsOpen && chartsPinned) ? 1 : 0) +
                  ((preferencesOpen && preferencesPinned) ? 1 : 0) +
                  ((filtersOpen && filtersPinned) ? 1 : 0) +
                  ((editingOpen && editingPinned) ? 1 : 0) +
                  ((statisticsOpen && statisticsPinned) ? 1 : 0) +
                  ((importOpen && importPinned) ? 1 : 0)
                } />
                <TileLayer
                  attribution='&copy; OpenStreetMap contributors'
                  url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                  maxNativeZoom={19}
                  maxZoom={25}
                />
                <Pane name="paths" style={{ zIndex: 200 }} />
                <Pane name="markers" style={{ zIndex: 400 }} />
                <Pane name="selection" style={{ zIndex: 500 }} />
                <Pane name="selectedMarkers" style={{ zIndex: 600 }} />
                {preferences.pathType !== 'none' && (
                  <StreamingPath
                    ref={livePathRef}
                    color={red[500]}
                    weight={4}
                    opacity={1}
                    pane={"paths"}
                  />
                )}
                {!isFlying && (
                  <>
                    {!isStreaming && (
                      <>
                        <MapPathAndMarkerLayer
                          allPositions={filteredPositionsWithCalculatedAttributes}
                          visiblePositions={visiblePositions}
                          visiblePathSegments={visiblePathSegments}
                          groupingConfig={groupingConfig}
                          onMarkerClick={(position) => {
                            ignoreNextPopupAutoPanRef.current = true;
                            setSelectedPosition(position);
                          }}
                          showSelection={editingOpen}
                          showPath={preferences.pathType !== 'none'}
                          showMarker={preferences.markerType !== 'none'}
                        />
                        {selectedPosition && (
                          <PositionPopup
                            position={selectedPosition}
                            onClose={() => setSelectedPosition(null)}
                            openDrawerEditing={() => setEditingOpen(true)}
                            pinDrawerEditing={() => setEditingPinned(true)}
                            viewportHeight={viewportHeight}
                            viewportWidth={viewportWidth}
                          />
                        )}
                      </>
                    )}
                    {filteredPois.map((poi: { latitude: any; longitude: any; displayRadius: any; color: any; icon: any; }, i: any) => (
                      <>
                        <Circle
                          key={`poi-circle-${i}`}
                          center={[poi.latitude, poi.longitude]}
                          radius={poi.displayRadius}
                          pathOptions={{
                            color: poi.color,
                            fillColor: poi.color,
                            fillOpacity: 0.3,
                            dashArray: '8 6'
                          }}
                        />
                        <Marker
                          key={`poi-marker-${i}`}
                          position={[poi.latitude, poi.longitude]}
                          icon={createMarkerIcon(poi.icon || 'place', poi.color || 'rgb(239, 83, 80)')}
                          interactive={false}
                        />
                      </>
                    ))}
                  </>
                )}
              </MapContainer>
            }
            {/*)*/}
          </Box>

          {/* Sidebar Drawer with Stats */}
          <CustomDrawer
            title="Statistics"
            icon={Icons.drawer.heading.statistics}
            anchor="right"
            open={statisticsOpen}
            onClose={() => toggleDrawer('statistics')}
            pinned={statisticsPinned}
            onTogglePinned={() => setStatisticsPinned((p: any) => !p)}
            width="20vw"
            minWidth="320px"
            height={`${viewportHeight}px`}
            minHeight={`${viewportHeight}px`}
            top="0px"
            bottom="0px"
            right={`calc(${(preferencesPinned && preferencesOpen) + (filtersPinned && filtersOpen) + (editingPinned && editingOpen) + (importPinned && importOpen)} * max(20vw, 320px) + 56px)`}
          >
            <PathStatistics
              data={pathStatistics}
            />
          </CustomDrawer>

          {/* Sidebar Drawer for Editing */}
          <CustomDrawer
            title="Editing"
            icon={Icons.drawer.heading.editing}
            anchor="right"
            open={editingOpen}
            onClose={() => toggleDrawer('editing')}
            pinned={editingPinned}
            onTogglePinned={() => setEditingPinned((p: any) => !p)}
            width="20vw"
            minWidth="320px"
            height={`${viewportHeight}px`}
            minHeight={`${viewportHeight}px`}
            top="0px"
            bottom="0px"
            right={`calc(${(preferencesPinned && preferencesOpen) + (filtersPinned && filtersOpen) + (importPinned && importOpen)} * max(20vw, 320px) + 56px)`}
          >
            <PositionEditing
              positions={filteredPositionsWithCalculatedAttributes}
              filterStartTime={startTime}
              filterEndTime={endTime}
              drawerOpen={editingOpen}
              closeDrawerEditing={() => setEditingOpen(false)}
              unpinDrawerEditing={() => setEditingPinned(false)}
              onPositionsUpdated={(positionIds, targetColumn, targetValue) => {
                const originalPositions = positions.filter(p => positionIds.includes(p.id));
                setPositions(prev => prev.map((position) =>
                  positionIds.includes(position.id)
                    ? ({ ...position, [targetColumn]: targetValue } as Position)
                    : position
                ));
                setFilters(prevFilters => {
                  const newFilters = { ...prevFilters };
                  originalPositions.forEach(origPos => {
                    const originalValue = origPos[targetColumn];
                    const newValue = targetValue;
                    if (targetColumn === 'time') return; // Skip date/time filters as they always have restrictions
                    if (['provider', 'source', 'travel_mode'].includes(targetColumn)) {
                      const filterList = newFilters[targetColumn] || [];
                      const currentValues = categoricalUniqueValues[targetColumn] || [];
                      const isAutoManagedCategorical =
                        Array.isArray(filterList) &&
                        currentValues.length > 0 &&
                        filterList.length === currentValues.length &&
                        currentValues.every((value) => filterList.includes(value));

                      if (isAutoManagedCategorical && newValue !== originalValue && !filterList.includes(newValue)) {
                        newFilters[targetColumn] = [...filterList, newValue];
                      }
                    } else if (['accuracy', 'altitude', 'speed', 'bearing', 'velocity', 'acceleration'].includes(targetColumn)) {
                      // Numeric filters are explicit if they are restricted.
                      // If the user has an active restriction, preserve it and do not auto-expand the range.
                    }
                  });
                  return newFilters;
                });
              }}
            />
          </CustomDrawer>

          {/* Import Drawer */}
          <CustomDrawer
            title="Import"
            icon={Icons.drawer.heading.import}
            anchor="right"
            open={importOpen}
            onClose={() => toggleDrawer('import')}
            pinned={importPinned}
            onTogglePinned={() => setImportPinned((p: any) => !p)}
            fullscreen={importFullscreen}
            onToggleFullscreen={() => setImportFullscreen((f: any) => !f)}
            width={importFullscreen ? `calc(100vw - 56px)` : "20vw"}
            minWidth={importFullscreen ? `calc(100vw - 56px)` : "320px"}
            height={`${viewportHeight}px`}
            minHeight={`${viewportHeight}px`}
            top="0px"
            bottom="0px"
            right={importFullscreen ? '56px' : `calc(${(preferencesPinned && preferencesOpen) + (filtersPinned && filtersOpen)} * max(20vw, 320px) + 56px)`}
            keepChildrenMounted
          >
            <ImportDrawer
              onImportSuccess={(trackId, trackName, importedStartTime, importedEndTime) => {
                console.log(`Successfully imported track ${trackId}: ${trackName}`);

                const startLocal = importedStartTime ? toDateTimeLocalValue(importedStartTime) : null;
                const endLocal = importedEndTime ? toDateTimeLocalValue(importedEndTime) : null;

                if (startLocal && endLocal) {
                  setStartTime(startLocal);
                  setEndTime(endLocal);
                }
              }}
            />
          </CustomDrawer>

          {/* Sidebar Drawer for Filters */}
          <CustomDrawer
            title="Filters"
            icon={Icons.drawer.heading.filters}
            anchor="right"
            open={filtersOpen}
            onClose={() => toggleDrawer('filters')}
            pinned={filtersPinned}
            onTogglePinned={() => setFiltersPinned((p: any) => !p)}
            width="20vw"
            minWidth="320px"
            height={`${viewportHeight}px`}
            minHeight={`${viewportHeight}px`}
            top="0px"
            bottom="0px"
            right={`calc(${(preferencesPinned && preferencesOpen) + 0} * max(20vw, 320px) + 56px)`}
          >
            <PathFilters
              data={positionsWithCalculatedAttributes}
              startTime={startTime}
              setStartTime={setStartTime}
              endTime={endTime}
              setEndTime={setEndTime}
              filters={filters}
              setFilters={setFilters}
              user={user}
              config={config}
            />
          </CustomDrawer>

          {/* Sidebar Drawer for Preferences */}
          <CustomDrawer
            title="Preferences"
            icon={Icons.drawer.heading.preferences}
            anchor="right"
            open={preferencesOpen}
            onClose={() => toggleDrawer('preferences')}
            pinned={preferencesPinned}
            onTogglePinned={() => setPreferencesPinned((p: any) => !p)}
            width="20vw"
            minWidth="320px"
            height={`${viewportHeight}px`}
            minHeight={`${viewportHeight}px`}
            top="0px"
            bottom="0px"
            right="56px"
          >
            <MapPreferences
              preferences={preferences}
              setPreferences={setPreferences}
              user={user}
              displayedUser={displayedUser}
              onSaveConfig={handleSaveConfig}
            />
          </CustomDrawer>

          <CustomDrawer
            title="Charts"
            icon={Icons.drawer.heading.charts}
            anchor="bottom"
            open={chartsOpen}
            onClose={() => toggleDrawer('charts')}
            pinned={chartsPinned}
            onTogglePinned={() => setChartsPinned((p: any) => !p)}
            fullscreen={chartsFullscreen}
            onToggleFullscreen={() => setChartsFullscreen((f: any) => !f)}
            width={`calc(100vw - ${(preferencesPinned && preferencesOpen) + (filtersPinned && filtersOpen) + (statisticsPinned && statisticsOpen) + (importPinned && importOpen) + (editingPinned && editingOpen)} * max(20vw, 320px) - 56px)`}
            minWidth={`calc(100vw - ${(preferencesPinned && preferencesOpen) + (filtersPinned && filtersOpen) + (statisticsPinned && statisticsOpen) + (importPinned && importOpen) + (editingPinned && editingOpen)} * max(20vw, 320px) - 56px)`}
            height="20vh"
            minHeight="350px"
            bottom="0px"
            left="0px"
            right={`calc(${(preferencesPinned && preferencesOpen) + (filtersPinned && filtersOpen) + (statisticsPinned && statisticsOpen) + (importPinned && importOpen) + (editingPinned && editingOpen)} * max(20vw, 320px) + 56px)`}
          >
            <Box sx={{ height: '100%', width: '100%', position: 'relative' }}>
              <PathCharts
                data={groupedAndFilteredPositionsWithCalculatedAttributesAndAggregatesCenteredOnGroupsAndPoisPreparedForPaths}
                aggregation={aggregation}
                width="100%"
                height={chartsFullscreen ? `calc(${viewportHeight}px - 88px)` : 'calc(max(20vh, 350px) - 88px)'}
              />
            </Box>
          </CustomDrawer>
        </PositionEditingProvider>
      )}

      {poiOpen && (
        <PoiManager
          user={user}
          displayedUser={displayedUser}
          pois={pois}
          setPois={setPois}
        />
      )}

      {tagsViewOpen && (
        <TagsManager
          isAdmin={!!user.admin}
          scopeUserId={displayedUser?.id ?? user.id}
          defaults={{
            path: {
              type: preferences.pathType,
              color: `#${preferences.pathColor}`,
              opacity: preferences.pathOpacity,
              weight: preferences.pathWeight,
              dash: preferences.pathDash,
              shadeChildren: preferences.pathShadeChildren,
            },
            marker: {
              type: preferences.markerType,
              radius: preferences.markerRadius,
              fillType: preferences.markerFillType,
              fillColor: `#${preferences.markerFillColor}`,
              fillOpacity: preferences.markerFillOpacity,
              borderType: preferences.markerBorderType,
              borderColor: `#${preferences.markerBorderColor}`,
              borderOpacity: preferences.markerBorderOpacity,
              borderWeight: preferences.markerBorderWeight,
              borderDash: preferences.markerBorderDash,
              shadeChildren: preferences.markerShadeChildren,
            },
          }}
        />
      )}

    </Box>
  );
};

const MapPage: React.FC<MapPageProps> = (props) => (
  <TagsProvider scopeUserId={props.displayedUser?.id ?? null}>
    <MapPageContent {...props} />
  </TagsProvider>
);

export default MapPage;
